import type { ThreatMapGeoLayer, ThreatMapGeoLayerProjection } from '@/types/map'
import { cn } from '@/lib/utils'

const LAYER_ORDER: ThreatMapGeoLayer[] = [
  'impact',
  'victim',
  'target',
  'inferred_impact',
  'regulatory',
  'fallback',
  'actor_origin',
  'advanced',
]

const LAYER_LABELS: Record<ThreatMapGeoLayer, string> = {
  impact: 'Impact geography',
  victim: 'Victim geography',
  target: 'Target geography',
  inferred_impact: 'Inferred impact',
  regulatory: 'Regulatory jurisdiction',
  fallback: 'Source/publisher fallback',
  actor_origin: 'Actor origin',
  advanced: 'Advanced geo context',
}

const TARGET_LAYERS = new Set<ThreatMapGeoLayer>([
  'impact',
  'victim',
  'target',
  'inferred_impact',
])

export interface GeoLayerLegendProps {
  layers?: ThreatMapGeoLayerProjection[]
}

export function GeoLayerLegend({ layers = [] }: GeoLayerLegendProps) {
  const grouped = LAYER_ORDER.map((geoLayer) => {
    const entries = layers.filter((layer) => layer.geoLayer === geoLayer)
    if (entries.length === 0) return null
    const rendersAsTarget = TARGET_LAYERS.has(geoLayer) && entries.some((layer) => layer.renderAsTarget)
    return { geoLayer, entries, rendersAsTarget }
  }).filter((group): group is NonNullable<typeof group> => group != null)

  if (grouped.length === 0) return null

  return (
    <div
      data-testid="geo-layer-legend"
      className="mt-1 flex flex-col gap-1 border-t border-[color:var(--border-subtle)] pt-1.5"
      aria-label="Geo layer legend"
    >
      {grouped.map(({ geoLayer, entries, rendersAsTarget }) => {
        const firstReason = entries.find((entry) => entry.reasonLabel)?.reasonLabel
        return (
          <div
            key={geoLayer}
            data-testid={`geo-layer-legend-${geoLayer}`}
            data-geo-layer={geoLayer}
            data-target-style={rendersAsTarget ? 'true' : 'false'}
            className="flex items-start gap-1.5"
            title={firstReason}
          >
            <span
              aria-hidden="true"
              className={cn(
                'mt-0.5 inline-block h-2.5 w-2.5 shrink-0',
                rendersAsTarget
                  ? 'rounded-full bg-[color:var(--interactive-primary)] opacity-80'
                  : 'rounded-sm border border-dashed border-[color:var(--text-tertiary)] bg-transparent',
              )}
            />
            <span className="min-w-0 flex-1">
              <span className="text-[color:var(--text-secondary)]">{LAYER_LABELS[geoLayer]}</span>
              <span className="ml-1 text-[color:var(--text-tertiary)]">×{entries.length}</span>
              {!rendersAsTarget && (
                <span className="ml-1 text-[color:var(--text-tertiary)]">context only</span>
              )}
            </span>
          </div>
        )
      })}
    </div>
  )
}
